import { CheckSquare } from "lucide-react";
import { useMemo } from "react";
import { useGetIdentity, useListContext, useTranslate } from "ra-core";
import { BulkActionsToolbar } from "@/components/admin/bulk-actions-toolbar";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { SortButton } from "@/components/admin/sort-button";

import { TopToolbar } from "../layout/TopToolbar";
import { useIsAdmin } from "../sales/useIsAdmin";
import { AddTask } from "./AddTask";
import {
  DEFAULT_TASK_DUE_BUCKETS,
  getSelectedTaskDueBucket,
} from "./taskDueBuckets";
import { TaskSelectionContext } from "./TaskSelectionContext";
import { TasksBulkActions } from "./TasksBulkActions";
import { TasksListByDueDate } from "./TasksListByDueDate";
import { TasksListFilters } from "./TasksListFilters";

/**
 * Full-width task list page.
 *
 * Built on `<List>` so it inherits the filter rail, sort, URL sync and
 * selection every other resource page has. The rows themselves stay grouped by
 * due date — that grouping is what makes the page answer "what do I do next?"
 * rather than "here is a table of tasks".
 */
export const TasksListPage = () => {
  const { identity } = useGetIdentity();
  const { isAdmin, isPending } = useIsAdmin();

  // Rendering before admin status resolves flashes the wrong scope and costs an
  // extra query.
  if (isPending || !identity) return null;

  return (
    <List
      title={false}
      perPage={25}
      sort={{ field: "due_date", order: "ASC" }}
      // Non-administrators are pinned to their own tasks: a permanent filter,
      // not a default one, so it cannot be cleared from the rail or the URL.
      filter={isAdmin ? undefined : { sales_id: identity.id }}
      actions={<TasksListActions />}
      // The buckets paginate themselves; a page-wide pager would contradict them.
      pagination={null}
    >
      <TasksListLayout isAdmin={isAdmin} />
    </List>
  );
};

const TasksListLayout = ({ isAdmin }: { isAdmin: boolean }) => {
  const translate = useTranslate();
  const { filterValues, sort, selectedIds, onToggleItem } = useListContext();

  // Narrowing the rail to one due bucket renders that bucket alone, which is
  // the flat filtered list — with its heading and count kept.
  const selectedBucket = getSelectedTaskDueBucket(filterValues);
  const buckets = selectedBucket ? [selectedBucket] : DEFAULT_TASK_DUE_BUCKETS;

  const selection = useMemo(
    () => ({ selectedIds: selectedIds ?? [], onToggleItem }),
    [selectedIds, onToggleItem],
  );

  return (
    <div className="w-full flex flex-col lg:flex-row gap-8">
      <TasksListFilters isAdmin={isAdmin} />
      <div className="flex flex-1 flex-col gap-4 min-w-0">
        <div className="flex items-center">
          <div className="mr-3 flex">
            <CheckSquare className="text-muted-foreground w-6 h-6" />
          </div>
          <h1 className="text-xl font-semibold text-muted-foreground flex-1">
            {translate("resources.tasks.name", { smart_count: 2 })}
          </h1>
          <AddTask display="icon" selectContact />
        </div>
        <TaskSelectionContext.Provider value={isAdmin ? selection : null}>
          <TasksListByDueDate
            scope={isAdmin ? "all" : "mine"}
            groupByOwner={isAdmin}
            buckets={buckets}
            filter={filterValues}
            sort={sort}
            emptyPlaceholder={
              <p className="text-sm">
                {translate(
                  isAdmin
                    ? "resources.tasks.empty_list_hint_all"
                    : "resources.tasks.empty_list_hint",
                )}
              </p>
            }
          />
        </TaskSelectionContext.Provider>
      </div>
      {isAdmin && (
        <BulkActionsToolbar>
          <TasksBulkActions />
        </BulkActionsToolbar>
      )}
    </div>
  );
};

const TasksListActions = () => (
  <TopToolbar>
    <SortButton fields={["due_date", "type", "text"]} />
    <ExportButton />
  </TopToolbar>
);
